<template>
  <div class="modal-overlay" @mousedown.self="$emit('close')">
    <div class="modal-panel" role="dialog" aria-modal="true">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">{{ mode === 'create' ? $t('admin.bonus_form.title_create') : $t('admin.bonus_form.title_edit') }}</h2>
        <button class="modal-close" title="Close" @click="$emit('close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <form class="modal-body" @submit.prevent="handleSubmit">
        <!-- Basic Fields -->
        <div class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_campaign_details') }}</h3>

          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_campaign_name') }}
              <input
                v-model="form.name"
                type="text"
                class="form-input"
                placeholder="e.g. Summer Welcome Package"
                required
              />
            </label>
          </div>

          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_type') }}
              <select v-model="form.type" class="form-select" :disabled="mode === 'edit'" required>
                <option value="">{{ $t('admin.bonus_form.field_type_select') }}</option>
                <option value="welcome">{{ $t('admin.bonus_form.field_type_welcome') }}</option>
                <option value="deposit">{{ $t('admin.bonus_form.field_type_deposit') }}</option>
                <option value="no_deposit">{{ $t('admin.bonus_form.field_type_no_deposit') }}</option>
                <option value="free_spins">{{ $t('admin.bonus_form.field_type_free_spins') }}</option>
                <option value="cashback">{{ $t('admin.bonus_form.field_type_cashback') }}</option>
                <option value="reload">{{ $t('admin.bonus_form.field_type_reload') }}</option>
                <option value="promo_code">{{ $t('admin.bonus_form.field_type_promo_code') }}</option>
              </select>
              <span v-if="mode === 'edit'" class="form-hint">{{ $t('admin.bonus_form.field_type_hint') }}</span>
            </label>

            <label class="form-label">
              {{ $t('admin.bonus_form.field_status') }}
              <select v-model="form.status" class="form-select" required>
                <option value="draft">{{ $t('admin.bonus_form.field_status_draft') }}</option>
                <option value="active">{{ $t('admin.bonus_form.field_status_active') }}</option>
                <option value="paused">{{ $t('admin.bonus_form.field_status_paused') }}</option>
                <option v-if="mode === 'edit'" value="expired">{{ $t('admin.bonus_form.field_status_expired') }}</option>
              </select>
            </label>
          </div>

          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_starts_at') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model="form.starts_at" type="datetime-local" class="form-input" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_expires_at') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model="form.expires_at" type="datetime-local" class="form-input" />
            </label>
          </div>

          <div class="form-row">
            <label class="form-label" style="max-width: 260px">
              {{ $t('admin.bonus_form.field_max_claims') }} <span class="form-optional">{{ $t('admin.bonus_form.field_max_claims_hint') }}</span>
              <input
                v-model.number="form.max_claims"
                type="number"
                class="form-input"
                min="1"
                step="1"
                :placeholder="$t('admin.bonus_form.field_max_claims_placeholder')"
              />
            </label>
          </div>
        </div>

        <!-- Config: welcome -->
        <div v-if="form.type === 'welcome'" class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_config') }}</h3>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_bonus_amount') }}
              <input v-model.number="configDisplayForm.welcome_amount" type="number" min="0.01" step="0.01" class="form-input" required placeholder="e.g. 100" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_wagering_multiplier') }}
              <input v-model.number="form.config.wagering_multiplier" type="number" min="1" step="1" class="form-input" required placeholder="e.g. 35" />
              <span class="form-hint">{{ $t('admin.bonus_form.field_wagering_hint') }}</span>
            </label>
          </div>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_max_cashout') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model.number="configDisplayForm.welcome_max_cashout" type="number" min="0.01" step="0.01" class="form-input" :placeholder="$t('admin.bonus_form.field_no_limit_placeholder')" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_max_bet') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model.number="configDisplayForm.welcome_max_bet" type="number" min="0.01" step="0.01" class="form-input" :placeholder="$t('admin.bonus_form.field_no_limit_placeholder')" />
            </label>
          </div>
        </div>

        <!-- Config: deposit / reload -->
        <div v-if="form.type === 'deposit' || form.type === 'reload'" class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_config') }}</h3>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_match_percentage') }}
              <input v-model.number="form.config.percentage" type="number" min="1" max="1000" step="1" class="form-input" required placeholder="e.g. 100" />
              <span class="form-hint">{{ $t('admin.bonus_form.field_match_percentage_hint') }}</span>
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_max_bonus') }}
              <input v-model.number="configDisplayForm.deposit_max_bonus" type="number" min="0.01" step="0.01" class="form-input" required placeholder="e.g. 200" />
            </label>
          </div>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_wagering_multiplier') }}
              <input v-model.number="form.config.wagering_multiplier" type="number" min="1" step="1" class="form-input" required placeholder="e.g. 35" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_min_deposit') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model.number="configDisplayForm.deposit_min_deposit" type="number" min="0.01" step="0.01" class="form-input" :placeholder="$t('admin.bonus_form.field_min_deposit_placeholder')" />
            </label>
          </div>
        </div>

        <!-- Config: no_deposit -->
        <div v-if="form.type === 'no_deposit'" class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_config') }}</h3>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_bonus_amount') }}
              <input v-model.number="configDisplayForm.nodeposit_amount" type="number" min="0.01" step="0.01" class="form-input" required placeholder="e.g. 10" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_wagering_multiplier') }}
              <input v-model.number="form.config.wagering_multiplier" type="number" min="1" step="1" class="form-input" required placeholder="e.g. 40" />
            </label>
          </div>
        </div>

        <!-- Config: free_spins -->
        <div v-if="form.type === 'free_spins'" class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_config') }}</h3>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_spins') }}
              <input v-model.number="form.config.spins" type="number" min="1" step="1" class="form-input" required placeholder="e.g. 50" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_game_slug') }}
              <input v-model="form.config.game_slug" type="text" class="form-input" required placeholder="e.g. starburst" />
              <span class="form-hint">{{ $t('admin.bonus_form.field_game_slug_hint') }}</span>
            </label>
          </div>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_wagering_multiplier') }}
              <input v-model.number="form.config.wagering_multiplier" type="number" min="1" step="1" class="form-input" required placeholder="e.g. 30" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_spin_value') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model.number="configDisplayForm.freespins_spin_value" type="number" min="0.01" step="0.01" class="form-input" :placeholder="$t('admin.bonus_form.field_spin_value_placeholder')" />
            </label>
          </div>
        </div>

        <!-- Config: cashback -->
        <div v-if="form.type === 'cashback'" class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_config') }}</h3>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_cashback_percentage') }}
              <input v-model.number="form.config.percentage" type="number" min="1" max="100" step="1" class="form-input" required placeholder="e.g. 10" />
              <span class="form-hint">{{ $t('admin.bonus_form.field_cashback_percentage_hint') }}</span>
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_period') }}
              <select v-model="form.config.period" class="form-select" required>
                <option value="daily">{{ $t('admin.bonus_form.field_period_daily') }}</option>
                <option value="weekly">{{ $t('admin.bonus_form.field_period_weekly') }}</option>
                <option value="monthly">{{ $t('admin.bonus_form.field_period_monthly') }}</option>
              </select>
            </label>
          </div>
          <div class="form-row">
            <label class="form-label" style="max-width: 260px">
              {{ $t('admin.bonus_form.field_max_cashback') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model.number="configDisplayForm.cashback_max_cashback" type="number" min="0.01" step="0.01" class="form-input" :placeholder="$t('admin.bonus_form.field_no_limit_placeholder')" />
            </label>
          </div>
        </div>

        <!-- Config: promo_code -->
        <div v-if="form.type === 'promo_code'" class="form-section">
          <h3 class="section-title">{{ $t('admin.bonus_form.section_config') }}</h3>
          <div class="form-row two-col">
            <label class="form-label">
              {{ $t('admin.bonus_form.field_promo_code') }}
              <input v-model="form.config.code" type="text" class="form-input" required placeholder="e.g. SUMMER25" style="text-transform: uppercase" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_wagering_multiplier') }}
              <input v-model.number="form.config.wagering_multiplier" type="number" min="1" step="1" class="form-input" required placeholder="e.g. 30" />
            </label>
          </div>
          <div class="form-row">
            <label class="form-label">{{ $t('admin.bonus_form.field_reward_type') }}</label>
            <div class="radio-group">
              <label class="radio-label">
                <input v-model="promoRewardType" type="radio" value="amount" class="radio-input" />
                {{ $t('admin.bonus_form.field_reward_fixed') }}
              </label>
              <label class="radio-label">
                <input v-model="promoRewardType" type="radio" value="percentage" class="radio-input" />
                {{ $t('admin.bonus_form.field_reward_percentage') }}
              </label>
            </div>
          </div>
          <div class="form-row two-col">
            <label v-if="promoRewardType === 'amount'" class="form-label">
              {{ $t('admin.bonus_form.field_bonus_amount') }}
              <input v-model.number="configDisplayForm.promo_amount" type="number" min="0.01" step="0.01" class="form-input" required placeholder="e.g. 50" />
            </label>
            <label v-if="promoRewardType === 'percentage'" class="form-label">
              {{ $t('admin.bonus_form.field_reward_percentage') }}
              <input v-model.number="form.config.percentage" type="number" min="1" max="1000" step="1" class="form-input" required placeholder="e.g. 50" />
            </label>
            <label class="form-label">
              {{ $t('admin.bonus_form.field_max_uses') }} <span class="form-optional">{{ $t('admin.bonus_form.field_optional') }}</span>
              <input v-model.number="form.config.max_uses" type="number" min="1" step="1" class="form-input" :placeholder="$t('admin.bonus_form.field_max_claims_placeholder')" />
            </label>
          </div>
        </div>

        <!-- Error / success banners -->
        <div v-if="errorMsg" class="error-banner">{{ errorMsg }}</div>

        <!-- Footer actions -->
        <div class="modal-footer">
          <button type="button" class="btn btn-ghost" :disabled="saving" @click="$emit('close')">{{ $t('admin.bonus_form.btn_cancel') }}</button>
          <button type="submit" class="btn btn-primary" :disabled="saving || !form.type">
            <span v-if="saving" class="spinner-sm"></span>
            {{ saving ? $t('admin.bonus_form.btn_saving') : (mode === 'create' ? $t('admin.bonus_form.btn_create') : $t('admin.bonus_form.btn_save')) }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

interface ConfigSummary {
  amount?: number
  percentage?: number
  max_bonus?: number
  spins?: number
  wagering_multiplier?: number
  max_cashout?: number
  max_bet?: number
  max_cashback?: number
  period?: string
  game_slug?: string
  code?: string
  max_uses?: number
  spin_value?: number
  min_deposit?: number
}

interface AdminBonus {
  id: number
  name: string
  type: string
  config_summary: ConfigSummary
  status: string
  times_claimed: number
  max_claims: number | null
  starts_at: string | null
  expires_at: string | null
  created_at: string | null
}

interface BonusConfig {
  // shared
  wagering_multiplier?: number | null
  percentage?: number | null
  period?: string | null
  // free_spins
  spins?: number | null
  game_slug?: string | null
  // promo_code
  code?: string | null
  max_uses?: number | null
}

const props = defineProps<{
  mode: 'create' | 'edit'
  bonus: AdminBonus | null
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'close'): void
}>()

const { t } = useI18n()

const saving = ref(false)
const errorMsg = ref('')

// For promo_code: track whether reward is amount or percentage
const promoRewardType = ref<'amount' | 'percentage'>('amount')

// Main form state
const form = reactive({
  name: '',
  type: '',
  status: 'draft' as string,
  starts_at: '',
  expires_at: '',
  max_claims: null as number | null,
  config: {} as BonusConfig & Record<string, unknown>,
})

// Dollar-display fields: cents ↔ dollars conversion
const configDisplayForm = reactive({
  // welcome
  welcome_amount: null as number | null,
  welcome_max_cashout: null as number | null,
  welcome_max_bet: null as number | null,
  // deposit / reload
  deposit_max_bonus: null as number | null,
  deposit_min_deposit: null as number | null,
  // no_deposit
  nodeposit_amount: null as number | null,
  // free_spins
  freespins_spin_value: null as number | null,
  // cashback
  cashback_max_cashback: null as number | null,
  // promo_code
  promo_amount: null as number | null,
})

// Convert ISO datetime to datetime-local value (strip seconds/tz)
function toDatetimeLocal(iso: string | null): string {
  if (!iso) return ''
  // "2025-06-01T00:00:00.000000Z" → "2025-06-01T00:00"
  return iso.slice(0, 16)
}

// Populate form from existing bonus (edit mode)
function populateFromBonus(b: AdminBonus) {
  form.name = b.name
  form.type = b.type
  form.status = b.status
  form.starts_at = toDatetimeLocal(b.starts_at)
  form.expires_at = toDatetimeLocal(b.expires_at)
  form.max_claims = b.max_claims

  const s = b.config_summary

  // Reset display fields
  Object.assign(configDisplayForm, {
    welcome_amount: null,
    welcome_max_cashout: null,
    welcome_max_bet: null,
    deposit_max_bonus: null,
    deposit_min_deposit: null,
    nodeposit_amount: null,
    freespins_spin_value: null,
    cashback_max_cashback: null,
    promo_amount: null,
  })

  switch (b.type) {
    case 'welcome':
      configDisplayForm.welcome_amount = s.amount != null ? s.amount / 100 : null
      configDisplayForm.welcome_max_cashout = s.max_cashout != null ? s.max_cashout / 100 : null
      configDisplayForm.welcome_max_bet = s.max_bet != null ? (s.max_bet as number) / 100 : null
      form.config = { wagering_multiplier: s.wagering_multiplier ?? null }
      break
    case 'deposit':
    case 'reload':
      configDisplayForm.deposit_max_bonus = s.max_bonus != null ? s.max_bonus / 100 : null
      configDisplayForm.deposit_min_deposit = s.min_deposit != null ? s.min_deposit / 100 : null
      form.config = {
        percentage: s.percentage ?? null,
        wagering_multiplier: s.wagering_multiplier ?? null,
      }
      break
    case 'no_deposit':
      configDisplayForm.nodeposit_amount = s.amount != null ? s.amount / 100 : null
      form.config = { wagering_multiplier: s.wagering_multiplier ?? null }
      break
    case 'free_spins':
      configDisplayForm.freespins_spin_value = s.spin_value != null ? s.spin_value / 100 : null
      form.config = {
        spins: s.spins ?? null,
        game_slug: s.game_slug ?? null,
        wagering_multiplier: s.wagering_multiplier ?? null,
      }
      break
    case 'cashback':
      configDisplayForm.cashback_max_cashback = s.max_cashback != null ? s.max_cashback / 100 : null
      form.config = {
        percentage: s.percentage ?? null,
        period: s.period ?? 'weekly',
      }
      break
    case 'promo_code':
      if (s.amount != null) {
        promoRewardType.value = 'amount'
        configDisplayForm.promo_amount = s.amount / 100
      } else if (s.percentage != null) {
        promoRewardType.value = 'percentage'
        form.config.percentage = s.percentage
      }
      form.config = {
        code: s.code ?? null,
        wagering_multiplier: s.wagering_multiplier ?? null,
        max_uses: s.max_uses ?? null,
        percentage: s.percentage ?? null,
      }
      break
  }
}

// Reset form for create mode
function resetForm() {
  form.name = ''
  form.type = ''
  form.status = 'draft'
  form.starts_at = ''
  form.expires_at = ''
  form.max_claims = null
  form.config = {}
  promoRewardType.value = 'amount'
  Object.assign(configDisplayForm, {
    welcome_amount: null,
    welcome_max_cashout: null,
    welcome_max_bet: null,
    deposit_max_bonus: null,
    deposit_min_deposit: null,
    nodeposit_amount: null,
    freespins_spin_value: null,
    cashback_max_cashback: null,
    promo_amount: null,
  })
}

// Watch bonus prop to populate form
watch(
  () => props.bonus,
  (b) => {
    if (b) {
      populateFromBonus(b)
    } else {
      resetForm()
    }
  },
  { immediate: true },
)

// Watch type changes in create mode to reset config
watch(
  () => form.type,
  (newType, oldType) => {
    if (props.mode === 'create' && newType !== oldType) {
      form.config = {}
    }
  },
)

// Build the config payload (converting dollars → cents for amount fields)
function buildConfig(): Record<string, unknown> {
  const cfg: Record<string, unknown> = {}

  switch (form.type) {
    case 'welcome':
      if (configDisplayForm.welcome_amount != null) cfg.amount = Math.round(configDisplayForm.welcome_amount * 100)
      cfg.wagering_multiplier = form.config.wagering_multiplier
      if (configDisplayForm.welcome_max_cashout != null) cfg.max_cashout = Math.round(configDisplayForm.welcome_max_cashout * 100)
      if (configDisplayForm.welcome_max_bet != null) cfg.max_bet = Math.round(configDisplayForm.welcome_max_bet * 100)
      break
    case 'deposit':
    case 'reload':
      cfg.percentage = form.config.percentage
      if (configDisplayForm.deposit_max_bonus != null) cfg.max_bonus = Math.round(configDisplayForm.deposit_max_bonus * 100)
      cfg.wagering_multiplier = form.config.wagering_multiplier
      if (configDisplayForm.deposit_min_deposit != null) cfg.min_deposit = Math.round(configDisplayForm.deposit_min_deposit * 100)
      break
    case 'no_deposit':
      if (configDisplayForm.nodeposit_amount != null) cfg.amount = Math.round(configDisplayForm.nodeposit_amount * 100)
      cfg.wagering_multiplier = form.config.wagering_multiplier
      break
    case 'free_spins':
      cfg.spins = form.config.spins
      cfg.game_slug = form.config.game_slug
      cfg.wagering_multiplier = form.config.wagering_multiplier
      if (configDisplayForm.freespins_spin_value != null) cfg.spin_value = Math.round(configDisplayForm.freespins_spin_value * 100)
      break
    case 'cashback':
      cfg.percentage = form.config.percentage
      cfg.period = form.config.period
      if (configDisplayForm.cashback_max_cashback != null) cfg.max_cashback = Math.round(configDisplayForm.cashback_max_cashback * 100)
      break
    case 'promo_code':
      cfg.code = form.config.code
      cfg.wagering_multiplier = form.config.wagering_multiplier
      if (form.config.max_uses) cfg.max_uses = form.config.max_uses
      if (promoRewardType.value === 'amount' && configDisplayForm.promo_amount != null) {
        cfg.amount = Math.round(configDisplayForm.promo_amount * 100)
      } else if (promoRewardType.value === 'percentage') {
        cfg.percentage = form.config.percentage
      }
      break
  }

  return cfg
}

// Build ISO datetime for API (datetime-local → UTC ISO)
function toIso(dtLocal: string): string | null {
  if (!dtLocal) return null
  return new Date(dtLocal).toISOString()
}

async function handleSubmit() {
  errorMsg.value = ''
  saving.value = true

  try {
    const payload: Record<string, unknown> = {
      name: form.name,
      type: form.type,
      config: buildConfig(),
      status: form.status,
      starts_at: toIso(form.starts_at),
      expires_at: toIso(form.expires_at),
      max_claims: form.max_claims || null,
    }

    if (props.mode === 'create') {
      await apiClient.post('/admin/api/bonuses', payload)
    } else if (props.bonus) {
      // type cannot change — exclude it from update payload
      delete payload.type
      await apiClient.put(`/admin/api/bonuses/${props.bonus.id}`, payload)
    }

    emit('saved')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
    if (e.response?.data?.errors) {
      const firstError = Object.values(e.response.data.errors)[0]
      errorMsg.value = Array.isArray(firstError) ? firstError[0] : String(firstError)
    } else {
      errorMsg.value = e.response?.data?.message ?? t('admin.bonus_form.error_save_failed')
    }
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/* Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}

/* Panel */
.modal-panel {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  width: 100%;
  max-width: 620px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md, 8px);
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-tertiary, #7f8fa4);
  cursor: pointer;
  transition: all 150ms ease;
  flex-shrink: 0;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-primary, #fff);
}

/* Body */
.modal-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Sections */
.form-section {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 18px;
}

.section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 0 14px;
}

/* Form rows */
.form-row {
  margin-bottom: 12px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-row.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary, #b1bad3);
}

.form-optional {
  font-weight: 400;
  opacity: 0.65;
}

.form-hint {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  opacity: 0.8;
}

.form-input,
.form-select {
  padding: 9px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.55);
}

.form-input:disabled,
.form-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-select option {
  background: var(--bg-secondary, #1a2c38);
}

/* Radio group */
.radio-group {
  display: flex;
  gap: 20px;
  margin-top: 2px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: var(--text-primary, #fff);
  cursor: pointer;
}

.radio-input {
  accent-color: var(--accent-primary, #00e701);
  width: 15px;
  height: 15px;
}

/* Error banner */
.error-banner {
  background: rgba(237, 66, 69, 0.12);
  border: 1px solid rgba(237, 66, 69, 0.35);
  border-radius: 8px;
  color: #f87171;
  padding: 10px 14px;
  font-size: 13px;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:not(:disabled):hover {
  background: #00cc01;
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.3);
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.btn-ghost:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-primary, #fff);
}

/* Spinner */
.spinner-sm {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 640px) {
  .form-row.two-col {
    grid-template-columns: 1fr;
  }

  .modal-overlay {
    padding: 16px 8px;
  }
}
</style>
